import {
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import { addDebouncedCopy, addPropTrackingRef } from 'ad-hok-utils'
import cx from 'classnames'
import { FC } from 'react'
import { Trans } from 'react-i18next'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { addIntPathParam, addNavigate } from 'shared/src/utils/addNavigate'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'

import Icon from 'components/Icon'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { addCmsLoadingAndErrorIndicators } from '../../utils/addCmsLoadingAndErrorIndicators'
import { Page } from '../Page'
import { Stat } from '../myRodeo/Stat'
import {
  BODY_MEDIUM_STYLE,
  BUTTONS_STYLE,
  MAIN_TITLE_STYLE,
  SANS_SERIF_FONT_FAMILY,
} from '../styles'
import { Edit } from './Edit'
import { EditSearch } from './EditSearch'

const useStyles = createUseStyles({
  container: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
    marginBottom: 3,
    paddingLeft: 24,
    paddingRight: 24,
  },
  title: MAIN_TITLE_STYLE,
  newButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    width: 138,
    backgroundColor: 'rgba(54, 52, 50, 0.96)',
    ...BUTTONS_STYLE,
    color: 'white',
  },
  newButtonIcon: {
    marginRight: 8,
  },
  statsContainer: {
    display: 'flex',
    marginBottom: 25,
    columnGap: 50,
    paddingLeft: 24,
    paddingRight: 24,
  },
  inputContainer: {
    position: 'relative',
    marginLeft: 24,
    marginRight: 24,
  },
  searchIcon: {
    position: 'absolute',
    top: 44,
    right: 16,
    color: 'rgba(144, 115, 79, 0.56)',
  },
  input: {
    ...BODY_MEDIUM_STYLE,
    width: '100%',
    height: 62,
    caretColor: '#90734F',
    outline: 'none',
    color: '#90734F',
    marginBottom: 24,
    marginTop: 24,
    paddingRight: 40,
    '&::placeholder': {
      color: 'rgba(54, 52, 50, 0.80)',
      opacity: 1,
    },
    borderRadius: 4,
    border: '1px solid rgba(144, 115, 79, 0.24)',
    background: 'rgba(54, 52, 50, 0.02)',
    '&:focus': {
      border: '1px solid rgba(144, 115, 79, 0.56)',
    },
  },
  resultText: {
    color: '#363432',
    fontSize: 14,
    fontWeight: 400,
    fontFamily: SANS_SERIF_FONT_FAMILY,
    marginLeft: 32,
  },
  boldText: {
    fontWeight: 700,
  },
  resultCountText: {
    color: 'rgba(54, 52, 50, 0.56)',
  },
})

export const Edits: FC = flowMax(
  addDisplayName('Edits'),
  addApi,
  addNavigate,
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  addIntPathParam('userId'),
  addDataLoadingFromPromise(
    'feed',
    ({ userId, api }) => api.userLists.feed(userId),
    []
  ),
  addCmsLoadingAndErrorIndicators('feed'),
  addState('newModalOpen', 'setNewModalOpen', false),
  addHandlers({
    onClickNew:
      ({ setNewModalOpen }) =>
      () => {
        setNewModalOpen(true)
      },
    onCloseNewModal:
      ({ setNewModalOpen }) =>
      () => {
        setNewModalOpen(false)
      },
  }),
  addState('searchResults', 'setSearchResults', ({ feed }) => feed.userLists),
  addState('searchText', 'onChangeSearchText', ''),
  addHandlers({
    onChange:
      ({ onChangeSearchText }) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChangeSearchText(event.target.value)
      },
  }),
  addDebouncedCopy(200, 'searchText', 'searchTextDebounced'),
  addPropTrackingRef('searchText', 'searchTextRef'),
  addEffect(
    ({ searchTextDebounced, feed, api, searchTextRef, setSearchResults }) =>
      () => {
        if (searchTextRef.current !== searchTextDebounced) return

        api.userLists
          .search(searchTextDebounced, feed.user.id)
          .then(setSearchResults)
      },
    ['searchTextDebounced']
  ),
  addTranslationHelpers,
  ({
    userId,
    feed: {
      user: { stats },
    },
    searchResults,
    onChange,
    searchText,
    searchTextDebounced,
    newModalOpen,
    onCloseNewModal,
    onClickNew,
    classes,
    t,
  }) => (
    <Page className={classes.container}>
      <div className={classes.header}>
        <h1 className={classes.title}>{t('admin.userEdits.title')}</h1>
        <button onClick={onClickNew} className={classes.newButtonContainer}>
          <Icon name="add" className={classes.newButtonIcon} />
          <span>{t('admin.userEdits.new')}</span>
        </button>
      </div>
      {stats && (
        <div className={classes.statsContainer}>
          <Stat
            statValue={stats.behindTheGateViews}
            statDescription={t('admin.userEdits.views')}
            type="number"
          />
        </div>
      )}
      <div className={classes.inputContainer}>
        <input
          placeholder={t('admin.userEdits.usedEditSearch.placeholder')}
          onChange={onChange}
          value={searchText}
          className={cx(classes.input, 'focus:ring-0')}
        />
        <Icon name="search" className={classes.searchIcon} />
      </div>
      {searchTextDebounced !== '' && (
        <div className={classes.resultText}>
          <Trans
            t={t}
            i18nKey={
              searchResults.length > 0
                ? 'admin.userEdits.usedEditSearch.results'
                : 'admin.userEdits.usedEditSearch.noResults'
            }
            values={{
              searchText: searchTextDebounced,
              count: searchResults.length,
            }}
          >
            <span className={classes.boldText} />
            <span className={classes.resultCountText} />
          </Trans>
        </div>
      )}
      {searchResults.map((list) => (
        <Edit list={list} userId={userId} key={list.id} />
      ))}
      {newModalOpen && <EditSearch onCancel={onCloseNewModal} />}
    </Page>
  )
)
